import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { Container, Row, Col, Icon } from '@pinpoint/design-kit';

import { Message, Button } from 'src/components';
import EqualBucketsSelector from 'src/components/models/equal_buckets_selector';
import { appActions, modelViewActions } from 'src/store/actions';
import { Page } from 'src/containers';
import CONSTANTS from 'src/store/constants';

import LiftDistribution from 'src/components/models/charts/lift_distribution';
import ValidationSample from 'src/components/models/charts/validation_sample';
import { ROUTES } from 'src/config';
import { queryHelper } from 'src/functions';

function ModelsValidationAnalytics({
  history,
  location,
  match,
  modelView,
  modelView: { equalType },
  fetchModel,
  setQuantiles,
  setChart,
  setGradient,
  fetchDownloadUrl,
  enqueueNotification,
  setEqualType,
  resetPage,
}) {
  const { search, pathname } = location;
  const {
    params: { modelId },
  } = match;
  const [chartType, setChartType] = useState(null);

  const getData = useCallback(() => {
    if (modelId) fetchModel(modelId);
  }, [modelId, fetchModel]);

  // When the tenant or the model id changes, retrieve the model anew
  useEffect(() => {
    getData();
  }, [getData]);

  useEffect(() => resetPage, [resetPage]);

  // When there's new query params parse for chart, redirect to lift-distribution if not explicitly set
  useEffect(() => {
    queryHelper(history, (query) => {
      const nextChartType = query.get('chart');
      if (!nextChartType) {
        query.append('chart', 'lift-distribution');
      } else {
        setChartType(nextChartType);
      }
      if (chartType === 'validation-sample') {
        query.delete('equal');
      }
    });
  }, [search, history, pathname, chartType]);

  return (
    <Container
      w="100%"
      minW="100%"
      h="100%"
      minH="100%"
      data-debug="container"
      d="flex"
      flexDir="column"
      overflow="hidden"
    >
      <Row data-debug="nav-row" p={{ t: '1.5rem' }} m={{ x: '.5rem' }}>
        <Col gutterWidth="0" d="inline-flex" size="12" align="center">
          <Button
            buttonStyle="primary"
            onClick={() => {
              history.push(ROUTES.MODELS);
            }}
          >
            <Icon name="LongLeft" color="white" m={{ r: '0.5rem' }} size="1rem" />
            BACK
          </Button>
          <Button
            buttonStyle="tab"
            selected={chartType === 'lift-distribution'}
            data-test="lift-dist-btn"
            onClick={() =>
              history.push(
                `${window.location.pathname}?chart=lift-distribution${equalType ? `&equal=${equalType}` : ''}`
              )
            }
          >
            <Icon name="DistributionChartEnclosed" size="1rem" color="white" viewBox="0 0 16 16" m={{ r: '.5rem' }} />
            LIFT DISTRIBUTION
          </Button>
          <Button
            buttonStyle="tab"
            selected={chartType === 'validation-sample'}
            data-test="validation-sample-btn"
            onClick={() => history.push(`${window.location.pathname}?chart=validation-sample`)}
          >
            <Icon name="BarChartHorizontalEnclosed" size="1rem" color="white" viewBox="0 0 16 17" m={{ r: '.5rem' }} />
            VALIDATION SAMPLE
          </Button>
          {chartType !== 'validation-sample' && (
            <EqualBucketsSelector
              models={[modelView?.model]}
              equalType={equalType}
              handleEqualTypeChange={setEqualType}
              equalCategory="Buckets"
            />
          )}
        </Col>
      </Row>
      <Row data-debug="content" p={{ t: '.5rem' }} m={{ x: '.5rem' }} h="100%" justify="stretch" align="stretch">
        {
          {
            [CONSTANTS.API.SUCCEEDED]: (
              <Col
                gutterWidth="0"
                size="12"
                bg="white"
                rounded="md"
                overflow="hidden"
                d="flex"
                flexDir="column"
                data-test="paper"
              >
                {
                  {
                    'lift-distribution': (
                      <LiftDistribution
                        modelView={modelView}
                        setChart={setChart}
                        setGradient={setGradient}
                        setQuantiles={setQuantiles}
                      />
                    ),
                    'validation-sample': (
                      <ValidationSample
                        enqueueNotification={enqueueNotification}
                        fetchDownloadUrl={fetchDownloadUrl}
                        modelView={modelView}
                      />
                    ),
                    default: <Message type="error" />,
                  }[chartType]
                }
              </Col>
            ),
            [CONSTANTS.API.FAILED]: <Message type="error" />,
            [CONSTANTS.API.STARTED]: <Message />,
            default: <Message default type="info" text="No data" cta={{ text: 'Refresh', onClick: getData }} />,
          }[modelView.fetchModelStatus]
        }
      </Row>
    </Container>
  );
}

const mapStateToProps = ({ app, modelView }, ownProps) => ({
  app,
  modelView,
  ...ownProps,
});

const mapDispatchToProps = (dispatch) => ({
  fetchModel: (...params) => dispatch(modelViewActions.fetchModel(...params)),
  setChart: (...params) => dispatch(modelViewActions.setChart(...params)),
  setGradient: (...params) => dispatch(modelViewActions.setGradient(...params)),
  setQuantiles: (...params) => dispatch(modelViewActions.setQuantiles(...params)),
  fetchDownloadUrl: (...params) => dispatch(modelViewActions.fetchDownloadUrl(...params)),
  enqueueNotification: (...params) => dispatch(appActions.enqueueNotification(...params)),
  setEqualType: (key) => dispatch(modelViewActions.setEqualType(key)),
  resetPage: () => dispatch(modelViewActions.resetPage()),
});

ModelsValidationAnalytics.propTypes = {
  history: PropTypes.shape({ push: PropTypes.func.isRequired }).isRequired,
  location: PropTypes.shape({ search: PropTypes.string.isRequired, pathname: PropTypes.string.isRequired }).isRequired,
  match: PropTypes.shape({ params: PropTypes.shape({ modelId: PropTypes.string.isRequired }) }).isRequired,
  app: PropTypes.shape({
    profile: PropTypes.shape({
      tenant: PropTypes.string.isRequired,
    }),
  }).isRequired,
  modelView: PropTypes.shape({
    equalType: PropTypes.oneOf([PropTypes.string, null]).isRequired,
    model: PropTypes.shape({}),
    fetchModelStatus: PropTypes.oneOf([PropTypes.string, undefined]).isRequired,
  }).isRequired,
  fetchModel: PropTypes.func.isRequired,
  setQuantiles: PropTypes.func.isRequired,
  setChart: PropTypes.func.isRequired,
  setGradient: PropTypes.func.isRequired,
  fetchDownloadUrl: PropTypes.func.isRequired,
  enqueueNotification: PropTypes.func.isRequired,
  setEqualType: PropTypes.func.isRequired,
  resetPage: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(Page(ModelsValidationAnalytics));
